import { useEffect, useState } from "react";
import styled from "styled-components";

// Click to select an icon, or drag on empty desktop to rubber-band select several.
// Works off data attributes so every icon type (apps, projects, folders) gets it for free:
//   [data-desktop-surface]  where a marquee drag may start
//   [data-desktop-icon]     selectable things
//   [data-window], [data-taskbar]  never start a marquee

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const getIcons = () =>
  Array.from(document.querySelectorAll<HTMLElement>("[data-desktop-icon]"));

const clearSelection = () =>
  getIcons().forEach((el) => el.removeAttribute("data-selected"));

const DesktopSelection = () => {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    let start: { x: number; y: number } | null = null;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;

      const icon = target.closest<HTMLElement>("[data-desktop-icon]");
      if (icon) {
        if (!e.ctrlKey && !e.metaKey) clearSelection();
        icon.setAttribute("data-selected", "true");
        return;
      }
      if (target.closest("[data-window], [data-taskbar]")) return;
      if (!target.closest("[data-desktop-surface]")) return;

      e.preventDefault();
      clearSelection();
      start = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!start) return;
      const next = {
        x: Math.min(start.x, e.clientX),
        y: Math.min(start.y, e.clientY),
        w: Math.abs(e.clientX - start.x),
        h: Math.abs(e.clientY - start.y),
      };
      setRect(next);

      getIcons().forEach((el) => {
        if (el.closest("[data-window]")) return;
        const r = el.getBoundingClientRect();
        const hit =
          r.left < next.x + next.w &&
          r.right > next.x &&
          r.top < next.y + next.h &&
          r.bottom > next.y;
        if (hit) el.setAttribute("data-selected", "true");
        else el.removeAttribute("data-selected");
      });
    };

    const handleMouseUp = () => {
      start = null;
      setRect(null);
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  if (!rect || rect.w + rect.h < 4) return null;

  return (
    <Marquee
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
    />
  );
};

export default DesktopSelection;

const Marquee = styled.div`
  position: fixed;
  z-index: 98000;
  pointer-events: none;
  border: 1px dotted #fff;
  outline: 1px dotted #000;
  background: rgba(0, 0, 123, 0.12);
`;
